import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';

function MediciNew() {
    // Data and loading states
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [meta, setMeta] = useState(null);

    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDoctor, setSelectedDoctor] = useState('');
    const [selectedCabinet, setSelectedCabinet] = useState('');
    const [fromDate, setFromDate] = useState(() => {
        // Default to today
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState(() => {
        // Default to 30 days from now
        const future = new Date();
        future.setDate(future.getDate() + 30);
        return future.toISOString().split('T')[0];
    });
    const [sortOrder, setSortOrder] = useState('medic'); // medic, -medic, cabinet, -cabinet, data, -data, ora_start, -ora_start

    const loadSchedules = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {
                q: searchQuery || undefined,
                id_medic: selectedDoctor || undefined,
                id_cabinet: selectedCabinet || undefined,
                from: fromDate,
                to: toDate,
                sort: sortOrder
            };

            // Remove undefined values
            Object.keys(params).forEach(key => {
                if (params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await apiService.getSpsAvailableSchedules(params);

            if (response.ok) {
                setSchedules(response.items || []);
                setMeta(response.meta || null);
            } else {
                setError('Eroare la încărcarea programelor disponibile. Vă rugăm să reîncercați.');
                setSchedules([]);
                setMeta(null);
            }
        } catch (err) {
            console.error('Error loading schedules:', err);
            setError('Eroare la încărcarea programelor disponibile. Vă rugăm să reîncercați.');
            setSchedules([]);
            setMeta(null);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedDoctor, selectedCabinet, fromDate, toDate, sortOrder]);

    // Load schedules on component mount and when filters change
    useEffect(() => {
        loadSchedules();
    }, [loadSchedules]);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ro-RO', {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (timeStr) => {
        // Remove seconds if present
        return timeStr.substring(0, 5);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setSelectedDoctor('');
        setSelectedCabinet('');
        setFromDate(() => {
            const today = new Date();
            return today.toISOString().split('T')[0];
        });
        setToDate(() => {
            const future = new Date();
            future.setDate(future.getDate() + 30);
            return future.toISOString().split('T')[0];
        });
        setSortOrder('medic');
    };

    // Note: Helper functions for potential future use with dropdown filters
    // const getUniqueDoctors = () => {
    //     const doctorMap = new Map();
    //     schedules.forEach(schedule => {
    //         if (!doctorMap.has(schedule.nume_medic)) {
    //             doctorMap.set(schedule.nume_medic, schedule.nume_medic);
    //         }
    //     });
    //     return Array.from(doctorMap.keys());
    // };

    // const getUniqueCabinets = () => {
    //     const cabinetMap = new Map();
    //     schedules.forEach(schedule => {
    //         if (!cabinetMap.has(schedule.cabinet)) {
    //             cabinetMap.set(schedule.cabinet, schedule.cabinet);
    //         }
    //     });
    //     return Array.from(cabinetMap.keys());
    // };

    return (
        <ErrorBoundary>
            <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
                {/* Header Section */}
                <div style={{
                    background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
                    color: 'white',
                    padding: '3rem 1rem',
                    textAlign: 'center'
                }}>
                    <h1 style={{
                        fontSize: '2.5rem',
                        fontWeight: '700',
                        marginBottom: '0.5rem',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        lineHeight: '1.2'
                    }}>
                        Program Medici Disponibili
                    </h1>
                    <p style={{
                        fontSize: '1.1rem',
                        opacity: '0.9',
                        marginBottom: '0',
                        textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                        lineHeight: '1.4'
                    }}>
                        Verifică disponibilitatea medicilor în perioada dorită (SPS API)
                    </p>
                </div>

                {/* Content Section */}
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: '2rem 1rem'
                }}>

                    {/* Error Message */}
                    {error && (
                        <div style={{
                            background: '#ffebee',
                            color: '#c62828',
                            padding: '1rem',
                            borderRadius: '8px',
                            marginBottom: '2rem',
                            border: '1px solid #ffcdd2'
                        }}>
                            <strong>Eroare:</strong> {error}
                        </div>
                    )}

                    {/* Filters Section */}
                    <div style={{
                        background: 'white',
                        padding: '2rem',
                        borderRadius: '12px',
                        marginBottom: '2rem',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: '1rem',
                            marginBottom: '1.5rem'
                        }}>
                            {/* Date Range */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#333'
                                }}>
                                    De la data:
                                </label>
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: 'white'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#333'
                                }}>
                                    Până la data:
                                </label>
                                <input
                                    type="date"
                                    value={toDate}
                                    min={fromDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: 'white'
                                    }}
                                />
                            </div>

                            {/* Search */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#333'
                                }}>
                                    Caută (medic/cabinet):
                                </label>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Ex: Maria, Cabinet 3..."
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: 'white'
                                    }}
                                />
                            </div>

                            {/* Sort */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#333'
                                }}>
                                    Sortează după:
                                </label>
                                <select
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    <option value="medic">Medic (A-Z)</option>
                                    <option value="-medic">Medic (Z-A)</option>
                                    <option value="cabinet">Cabinet (A-Z)</option>
                                    <option value="-cabinet">Cabinet (Z-A)</option>
                                    <option value="data">Dată crescător</option>
                                    <option value="-data">Dată descrescător</option>
                                    <option value="ora_start">Oră crescător</option>
                                    <option value="-ora_start">Oră descrescător</option>
                                </select>
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <button
                                onClick={clearFilters}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: '#f8f9fa',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem'
                                }}
                            >
                                Curăță filtre
                            </button>

                            {meta && (
                                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                    {meta.count > 0 ? (
                                        <>Programări disponibile: {meta.count} în intervalul {formatDate(meta.from)} - {formatDate(meta.to)}</>
                                    ) : (
                                        <>Nu s-au găsit programări disponibile</>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div style={{
                            textAlign: 'center',
                            padding: '3rem',
                            color: '#666'
                        }}>
                            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔄</div>
                            <p>Se încarcă programele disponibile...</p>
                        </div>
                    )}

                    {/* Schedules Grid */}
                    {!loading && schedules.length > 0 && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                            gap: '1.5rem',
                            marginBottom: '2rem'
                        }}>
                            {schedules.map((schedule, index) => (
                                <div
                                    key={`${schedule.nume_medic}-${schedule.data}-${schedule.ora_start}-${index}`}
                                    style={{
                                        background: 'white',
                                        padding: '1.5rem',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        border: '1px solid #e0e0e0',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-5px)';
                                        e.target.style.boxShadow = '0 8px 30px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    {/* Header */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '1rem'
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '1rem',
                                            color: 'white',
                                            marginRight: '0.75rem',
                                            fontWeight: 'bold'
                                        }}>
                                            👨‍⚕️
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <h3 style={{
                                                margin: '0 0 0.25rem 0',
                                                color: 'var(--color-primary)',
                                                fontSize: '1.1rem',
                                                fontWeight: '600',
                                                lineHeight: '1.2'
                                            }}>
                                                {schedule.nume_medic}
                                            </h3>
                                            <p style={{
                                                margin: '0',
                                                color: '#666',
                                                fontSize: '0.8rem'
                                            }}>
                                                {schedule.cabinet}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Date and Time */}
                                    <div style={{
                                        background: '#f8fafc',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        marginBottom: '1rem',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <span style={{
                                                fontWeight: '600',
                                                color: '#333',
                                                fontSize: '0.95rem'
                                            }}>
                                                📅 {formatDate(schedule.data)}
                                            </span>
                                        </div>
                                        <div style={{
                                            fontSize: '1.1rem',
                                            fontWeight: 'bold',
                                            color: 'var(--color-primary)',
                                            textAlign: 'center'
                                        }}>
                                            🕐 {formatTime(schedule.ora_start)} - {formatTime(schedule.ora_sfarsit)}
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div style={{
                                        position: 'absolute',
                                        top: '1rem',
                                        right: '1rem',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #4caf50, #45a049)',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase'
                                    }}>
                                        Disponibil
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && schedules.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '3rem',
                            background: 'white',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                        }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📅</div>
                            <h3 style={{ marginBottom: '0.5rem', color: '#333' }}>
                                Nicio programare disponibilă
                            </h3>
                            <p style={{ color: '#666', marginBottom: '2rem' }}>
                                Nu s-au găsit programări disponibile pentru filtrele selectate.
                            </p>
                            <button
                                onClick={clearFilters}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    background: 'var(--color-primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Arată toate
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </ErrorBoundary>
    );
}

export default MediciNew;
